// RegisterScaffold — shared CRUD register UI for Golden Thread / BSA / Fire Safety (Phase 6).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, X, Save, Loader2, Trash2, Search, Info, Paperclip, FileText, Download, Unlink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import DocumentLinkDialog from "@/components/DocumentLinkDialog";

const CAN_EDIT = new Set(["director", "administrator", "consultant", "client_admin", "site_manager"]);

export default function RegisterScaffold({ config }) {
    const { title, sectionTag, endpoint, testid, icon: Icon, columns, fields, filters = [], emptyHint,
            documentLink = false, guidance = null, showCompleteness = false } = config;
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [sites, setSites] = useState([]);
    const [projects, setProjects] = useState([]);
    const [meta, setMeta] = useState({});
    const [q, setQ] = useState("");
    const [filterVals, setFilterVals] = useState({});
    const [clientId, setClientId] = useState("");
    const [buildingId, setBuildingId] = useState("");
    const [siteId, setSiteId] = useState("");
    const [projectFilter, setProjectFilter] = useState("");
    const [portalVis, setPortalVis] = useState("");   // ''|'yes'|'no'
    const [criticalVal, setCriticalVal] = useState(""); // ''|'yes'|'no'
    const [ownerQ, setOwnerQ] = useState("");
    const [dueFilter, setDueFilter] = useState("");    // ''|'overdue'|'soon'
    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [denied, setDenied] = useState(false);
    const [guidanceOpen, setGuidanceOpen] = useState(() => {
        try { return localStorage.getItem(`pr_guidance_${testid}_dismissed`) !== "1"; } catch { return true; }
    });
    const dismissGuidance = () => {
        setGuidanceOpen(false);
        try { localStorage.setItem(`pr_guidance_${testid}_dismissed`, "1"); } catch { /* noop */ }
    };
    const reopenGuidance = () => {
        setGuidanceOpen(true);
        try { localStorage.removeItem(`pr_guidance_${testid}_dismissed`); } catch { /* noop */ }
    };
    const [docMap, setDocMap] = useState({});
    const [attachRow, setAttachRow] = useState(null);
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const canEdit = user && (config.moduleId
        ? (user.permissions ? user.permissions[config.moduleId] === "E" : CAN_EDIT.has(user.role))
        : CAN_EDIT.has(user.role));
    const lockedClient = portalClientId && !isInternalView;
    const projectId = params.get("project") || "";
    const [projectInfo, setProjectInfo] = useState(null);

    useEffect(() => {
        if (!projectId) { setProjectInfo(null); return; }
        api.get(`/projects/${projectId}`).then((r) => setProjectInfo(r.data)).catch(() => setProjectInfo(null));
    }, [projectId]);

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => {});
        api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
        api.get(`${endpoint}/meta`).then((r) => setMeta(r.data)).catch(() => {});
    }, [endpoint]);
    useEffect(() => { if (lockedClient) setClientId(portalClientId); }, [lockedClient, portalClientId]);

    const refresh = useCallback(async () => {
        try {
            const p = {};
            if (clientId) p.client_id = clientId;
            if (buildingId) p.building_id = buildingId;
            if (projectId) p.project_id = projectId;
            Object.entries(filterVals).forEach(([k, v]) => { if (v) p[k] = v; });
            const { data } = await api.get(endpoint, { params: p });
            setRows(data);
            setDenied(false);
        } catch (e) { if (e.response?.status === 403) setDenied(true); }
    }, [endpoint, clientId, buildingId, projectId, filterVals]);
    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (params.get("new") === "1" && canEdit) {
            setDialog({ open: true, mode: "create", initial: projectId ? { project_id: projectId, portal_visible: true } : null });
            params.delete("new"); setParams(params, { replace: true });
        }
    }, [params, canEdit, setParams, projectId]);

    const buildingMap = Object.fromEntries(buildings.map((b) => [b.id, b]));
    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const filteredBuildings = buildings.filter((b) => (!clientId || b.client_id === clientId) && (!siteId || b.site_id === siteId));
    const visProjects = projects.filter((p) => !clientId || p.client_id === clientId);

    // ---- Document link (Golden Thread Phase 2) ----
    useEffect(() => {
        if (!documentLink) return;
        const ids = [...new Set(rows.map((r) => r.document_id).filter((id) => id && !docMap[id]))];
        if (!ids.length) return;
        Promise.all(ids.map((id) => api.get(`/documents/${id}`).then((r) => [id, r.data]).catch(() => [id, null])))
            .then((pairs) => setDocMap((m) => ({ ...m, ...Object.fromEntries(pairs.filter(([, v]) => v)) })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentLink, rows]);

    const viewDocument = async (docId) => {
        try {
            const resp = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: resp.headers["content-type"] }));
            window.open(url, "_blank");
        } catch { toast.error("Could not open file"); }
    };

    const uploadAttach = async (row, file) => {
        const fd = new FormData();
        fd.append("file", file);
        if (row.document_id) {
            // Replacing an already-linked file: add a new revision to the SAME
            // Document (keeps its version history) rather than orphaning it.
            await api.post(`/documents/${row.document_id}/versions`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (config.documentLinkMarksCurrent) {
                await api.patch(`${endpoint}/${row.id}`, { status: "Current", last_reviewed: new Date().toISOString().slice(0, 10) });
            }
            setDocMap((m) => { const n = { ...m }; delete n[row.document_id]; return n; });
            toast.success(config.documentLinkMarksCurrent ? "New version uploaded — entry marked Current" : "New version uploaded");
        } else {
            await api.post(`${endpoint}/${row.id}/attach`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(config.documentLinkMarksCurrent ? "File attached — entry marked Current" : "File attached");
        }
        refresh();
    };

    const linkExisting = async (row, documentId) => {
        await api.post(`${endpoint}/${row.id}/link`, { document_id: documentId });
        toast.success(config.documentLinkMarksCurrent ? "Linked — entry marked Current" : "Linked");
        refresh();
    };

    const unlinkDocument = async (row) => {
        if (!window.confirm("Unlink this file from the register entry? The file itself stays in the Document Library.")) return;
        try {
            await api.delete(`${endpoint}/${row.id}/document`);
            toast.success("Unlinked");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Unlink failed"); }
    };

    const [completeness, setCompleteness] = useState([]);
    useEffect(() => {
        if (!showCompleteness) return;
        const p = {};
        if (clientId) p.client_id = clientId;
        if (buildingId) p.building_id = buildingId;
        if (projectId) p.project_id = projectId;
        api.get(`${endpoint}/completeness`, { params: p })
            .then((r) => setCompleteness(r.data))
            .catch(() => setCompleteness([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCompleteness, endpoint, clientId, buildingId, projectId, rows]);

    const OWNER_KEYS = ["owner", "responsible", "accountable_person"];
    const DUE_KEYS = ["next_due", "due_date", "next_review_date", "target_response_date", "last_inspected"];
    const todayStr = new Date().toISOString().slice(0, 10);
    const soonStr = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    const ownerOf = (r) => OWNER_KEYS.map((k) => r[k]).find((v) => v) || "";
    const dueOf = (r) => DUE_KEYS.map((k) => r[k]).find((v) => v) || "";

    const visible = rows.filter((r) => {
        if (q && !JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) return false;
        if (siteId && buildingMap[r.building_id]?.site_id !== siteId && r.site_id !== siteId) return false;
        if (projectFilter && r.project_id !== projectFilter) return false;
        if (portalVis === "yes" && !r.portal_visible) return false;
        if (portalVis === "no" && r.portal_visible) return false;
        if (criticalVal === "yes" && !r.critical) return false;
        if (criticalVal === "no" && r.critical) return false;
        if (ownerQ && !ownerOf(r).toLowerCase().includes(ownerQ.toLowerCase())) return false;
        if (dueFilter) {
            const d = (dueOf(r) || "").slice(0, 10);
            if (dueFilter === "overdue" && !(d && d < todayStr)) return false;
            if (dueFilter === "soon" && !(d && d >= todayStr && d <= soonStr)) return false;
        }
        return true;
    });
    const ctx = { buildingMap, clientMap };

    const accessors = useMemo(() => {
        const acc = {};
        columns.forEach((c) => {
            acc[c.key] = c.sortValue
                ? (r) => c.sortValue(r, ctx)
                : c.key === "building_id" ? (r) => buildingMap[r.building_id]?.name || ""
                : c.key === "client_id" ? (r) => clientMap[r.client_id]?.name || ""
                : (r) => r[c.key];
        });
        return acc;
    }, [columns, buildingMap, clientMap]); // eslint-disable-line react-hooks/exhaustive-deps
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(visible, { accessors });

    async function removeRow(row) {
        if (!window.confirm("Delete this entry?")) return;
        try {
            await api.delete(`${endpoint}/${row.id}`);
            toast.success("Entry deleted");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    }

    return (
        <div data-testid={`${testid}-page`} className="space-y-5">
            {denied ? (
                <div data-testid={`${testid}-denied`} className="bg-white border rounded-[11px] p-8 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <p className="text-[14px] font-semibold text-[#1F1F1F]">You don't have access to {title}.</p>
                </div>
            ) : (
            <>
            {projectId && (
                <div data-testid={`${testid}-project-banner`} className="flex items-center justify-between gap-3 flex-wrap rounded-[10px] border px-4 py-2.5" style={{ borderColor: "#E7C9A8", background: "#FBF1E6" }}>
                    <div className="text-[12px] font-semibold" style={{ color: "#8A5A1E" }}>
                        Filtered to project: {projectInfo?.name || projectId}
                    </div>
                    <button data-testid={`${testid}-back-to-project`} onClick={() => navigate(`/app/projects/${projectId}`)}
                        className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-[7px] bg-white border" style={{ borderColor: "#E7C9A8", color: "#8A5A1E" }}>
                        ← Back to project
                    </button>
                </div>
            )}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>{sectionTag}</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F] flex items-center gap-2">{Icon && <Icon className="w-5 h-5" style={{ color: "var(--brand-accent, #C75A1B)" }} />}{title}</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>{visible.length}{visible.length !== rows.length ? ` of ${rows.length}` : ""} entries</p>
                </div>
                {canEdit && (
                    <button data-testid={`${testid}-new-button`} onClick={() => setDialog({ open: true, mode: "create", initial: projectId ? { project_id: projectId, portal_visible: true } : null })}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus className="w-3.5 h-3.5" /> New entry
                    </button>
                )}
            </div>

            {guidance && (guidanceOpen ? (
                <div data-testid={`${testid}-guidance`} className="rounded-[10px] border" style={{ borderColor: "#D8E3E0", background: "#F3F8F6" }}>
                    <div className="w-full flex items-center justify-between gap-2 px-4 py-2.5">
                        <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold" style={{ color: "#2E6B57" }}>
                            <Info className="w-4 h-4" /> What is this register?
                        </span>
                        <button data-testid={`${testid}-guidance-dismiss`} onClick={dismissGuidance} title="Dismiss" className="p-0.5 rounded hover:bg-white/60">
                            <X className="w-4 h-4" style={{ color: "#2E6B57" }} />
                        </button>
                    </div>
                    <div className="px-4 pb-3.5 text-[12.5px] leading-relaxed" style={{ color: "#3A5750" }}>{guidance}</div>
                </div>
            ) : (
                <button data-testid={`${testid}-guidance-reopen`} onClick={reopenGuidance}
                    className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: "#2E6B57" }}>
                    <Info className="w-3.5 h-3.5" /> What is this register?
                </button>
            ))}

            {showCompleteness && completeness.length > 0 && (
                <div data-testid={`${testid}-completeness`} className="flex items-center gap-2.5 flex-wrap">
                    {completeness.map((c) => {
                        const s = c.by_status || {};
                        const active = buildingId === c.building_id;
                        return (
                            <button key={c.building_id} data-testid={`${testid}-completeness-${c.building_id}`}
                                onClick={() => setBuildingId(active ? "" : c.building_id)}
                                className="text-left bg-white border rounded-[9px] px-3.5 py-2 text-[12px] hover:shadow-sm transition-shadow"
                                style={{ borderColor: active ? "var(--brand-accent,#C75A1B)" : "#E5E0DA", background: active ? "#FBEFE5" : "#fff" }}>
                                <span className="font-semibold text-[#1F1F1F]">{buildingMap[c.building_id]?.name || "Unknown building"}</span>
                                <span style={{ color: "#6F6A64" }}> — {s.Current || 0} of {c.total} current</span>
                                {(s.Missing || 0) > 0 && <span className="ml-1.5 font-semibold" style={{ color: "#B33A2E" }}>· {s.Missing} missing</span>}
                                {(s.Superseded || 0) > 0 && <span className="ml-1.5" style={{ color: "#9A968F" }}>· {s.Superseded} superseded</span>}
                                {(s.Requested || 0) > 0 && <span className="ml-1.5" style={{ color: "#B8862B" }}>· {s.Requested} requested</span>}
                                {c.critical_missing > 0 && (
                                    <span className="ml-1.5 font-semibold" style={{ color: "#B33A2E" }}>· {c.critical_missing} critical gap{c.critical_missing !== 1 ? "s" : ""}</span>
                                )}
                            </button>
                        );
                    })}
                    {buildingId && completeness.some((c) => c.building_id === buildingId) && (
                        <button data-testid={`${testid}-completeness-clear`} onClick={() => setBuildingId("")}
                            className="text-[11.5px] font-semibold underline" style={{ color: "#6F6A64" }}>Clear building filter</button>
                    )}
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 bg-white border rounded-[8px] px-3 py-1.5 flex-1 min-w-[200px] max-w-[300px]" style={{ borderColor: "#E5E0DA" }}>
                    <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="flex-1 bg-transparent outline-none text-[12.5px]" data-testid={`${testid}-search`} />
                </div>
                <select data-testid={`${testid}-client-filter`} value={clientId} disabled={lockedClient}
                    onChange={(e) => { setClientId(e.target.value); setBuildingId(""); setSiteId(""); setProjectFilter(""); }}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">All clients</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {!lockedClient && (
                    <select data-testid={`${testid}-site-filter`} value={siteId} onChange={(e) => { setSiteId(e.target.value); setBuildingId(""); }}
                        className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                        <option value="">All sites</option>
                        {sites.filter((st) => !clientId || st.client_id === clientId).map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                    </select>
                )}
                <select data-testid={`${testid}-building-filter`} value={buildingId} onChange={(e) => setBuildingId(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">All buildings</option>
                    {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                {!lockedClient && !projectId && (
                    <select data-testid={`${testid}-project-filter`} value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}
                        className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                        <option value="">All projects</option>
                        {visProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                )}
                {filters.map((f) => (
                    <select key={f.param} data-testid={`${testid}-${f.param}-filter`} value={filterVals[f.param] || ""}
                        onChange={(e) => setFilterVals((v) => ({ ...v, [f.param]: e.target.value }))}
                        className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                        <option value="">{f.allLabel}</option>
                        {(meta[f.metaKey] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                ))}
                {!lockedClient && (
                    <>
                        <input data-testid={`${testid}-owner-filter`} value={ownerQ} onChange={(e) => setOwnerQ(e.target.value)}
                            placeholder="Owner…" className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm w-28" />
                        <select data-testid={`${testid}-due-filter`} value={dueFilter} onChange={(e) => setDueFilter(e.target.value)}
                            className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">Any due date</option>
                            <option value="overdue">Overdue</option>
                            <option value="soon">Due ≤ 30 days</option>
                        </select>
                        <select data-testid={`${testid}-portalvis-filter`} value={portalVis} onChange={(e) => setPortalVis(e.target.value)}
                            className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">Any visibility</option>
                            <option value="yes">Portal-visible</option>
                            <option value="no">Internal only</option>
                        </select>
                        <select data-testid={`${testid}-critical-filter`} value={criticalVal} onChange={(e) => setCriticalVal(e.target.value)}
                            className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">Any criticality</option>
                            <option value="yes">Critical only</option>
                            <option value="no">Non-critical</option>
                        </select>
                    </>
                )}
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-[12.7px]">
                    <thead className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                        <tr>
                            {columns.map((c) => (
                                <SortableTh key={c.key} myKey={c.key} sortKey={sortKey} sortDir={sortDir} onClick={toggleSort}
                                    testId={`${testid}-th-${c.key}`} className="group py-2.5">{c.label}</SortableTh>
                            ))}
                            {documentLink && <th className="py-2.5 text-left px-4">Document</th>}
                            {canEdit && <th></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.length === 0 && (
                            <tr><td colSpan={columns.length + 1 + (documentLink ? 1 : 0)} className="text-center py-10 italic" style={{ color: "#9A968F" }}>{emptyHint || "No entries yet."}</td></tr>
                        )}
                        {sorted.map((row) => (
                            <tr key={row.id} data-testid={`${testid}-row-${row.id}`} onClick={() => canEdit && setDialog({ open: true, mode: "edit", initial: row })}
                                className="border-t hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                {columns.map((c) => <td key={c.key} className="px-4 py-3 align-top">{c.render ? c.render(row, ctx) : (row[c.key] ?? "—")}</td>)}
                                {documentLink && (
                                    <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                                        {row.document_id ? (
                                            <div className="flex items-center gap-1.5" data-testid={`${testid}-doc-${row.id}`}>
                                                <button onClick={() => viewDocument(row.document_id)}
                                                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold max-w-[130px] truncate" style={{ color: "#2E6B57" }}
                                                    data-testid={`${testid}-doc-view-${row.id}`} title={docMap[row.document_id]?.title}>
                                                    <FileText className="w-3.5 h-3.5 flex-shrink-0" /> {docMap[row.document_id]?.title || "View"}
                                                </button>
                                                {canEdit && (
                                                    <>
                                                        <button onClick={() => setAttachRow(row)} title="Replace" data-testid={`${testid}-doc-replace-${row.id}`}
                                                            className="w-6 h-6 grid place-items-center rounded-[6px] hover:bg-[#F0EDE8] text-[#6F6A64]"><RefreshCw className="w-3 h-3" /></button>
                                                        <button onClick={() => unlinkDocument(row)} title="Unlink" data-testid={`${testid}-doc-unlink-${row.id}`}
                                                            className="w-6 h-6 grid place-items-center rounded-[6px] hover:bg-[#FBE8E5] text-[#B33A2E]"><Unlink className="w-3 h-3" /></button>
                                                    </>
                                                )}
                                            </div>
                                        ) : canEdit ? (
                                            <button onClick={() => setAttachRow(row)} data-testid={`${testid}-doc-attach-${row.id}`}
                                                className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1 rounded-[7px] border" style={{ borderColor: "var(--brand-accent,#C75A1B)", color: "var(--brand-accent,#C75A1B)" }}>
                                                <Paperclip className="w-3 h-3" /> Attach
                                            </button>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11.5px] italic" style={{ color: row.critical ? "#B33A2E" : "#9A968F" }} data-testid={`${testid}-doc-missing-${row.id}`}>
                                                <Paperclip className="w-3 h-3" /> No file
                                            </span>
                                        )}
                                    </td>
                                )}
                                {canEdit && (
                                    <td className="px-3 py-3 text-right">
                                        <button data-testid={`${testid}-delete-${row.id}`} onClick={(e) => { e.stopPropagation(); removeRow(row); }}
                                            className="w-8 h-8 grid place-items-center rounded-[7px] hover:bg-[#FBE8E5] text-[#B33A2E]"><Trash2 className="w-4 h-4" /></button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialog.open && (
                <RegisterDialog config={config} meta={meta} clients={clients} buildings={buildings}
                    mode={dialog.mode} initial={dialog.initial} lockedClientId={lockedClient ? portalClientId : null}
                    onClose={() => setDialog((d) => ({ ...d, open: false }))} onSaved={refresh} />
            )}
            {documentLink && attachRow && (
                <DocumentLinkDialog open={!!attachRow} clientId={attachRow.client_id}
                    title={attachRow.document_id ? "Replace file" : "Attach a file"}
                    onClose={() => setAttachRow(null)}
                    onUpload={(file) => uploadAttach(attachRow, file)}
                    onLink={(docId) => linkExisting(attachRow, docId)} />
            )}
            </>
            )}
        </div>
    );
}

function RegisterDialog({ config, meta, clients, buildings, mode, initial, lockedClientId, onClose, onSaved }) {
    const { endpoint, testid, fields, title } = config;
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const base = { client_id: lockedClientId || "", building_id: "" };
        fields.forEach((f) => { base[f.key] = f.default ?? ""; });
        setForm(initial ? { ...initial } : base);
    }, [initial, lockedClientId]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const filteredBuildings = buildings.filter((b) => !form.client_id || b.client_id === form.client_id);

    async function save() {
        if (mode === "create" && (!form.client_id || !form.building_id)) { toast.error("Client and building are required"); return; }
        for (const f of fields) {
            if (f.required && !String(form[f.key] || "").trim()) { toast.error(`${f.label} is required`); return; }
        }
        setBusy(true);
        try {
            if (mode === "create") {
                const payload = { client_id: form.client_id, building_id: form.building_id };
                fields.forEach((f) => { if (form[f.key] !== "" && form[f.key] != null) payload[f.key] = form[f.key]; });
                // carry project linkage when creating in a project context
                ["project_id", "phase_key", "phase_label"].forEach((k) => { if (form[k]) payload[k] = form[k]; });
                if (form.project_id) payload.portal_visible = form.portal_visible !== false;
                await api.post(endpoint, payload);
                toast.success(form.project_id ? "Entry created & linked to project" : "Entry created");
            } else {
                const payload = {};
                fields.forEach((f) => { if (form[f.key] !== "" && form[f.key] != null) payload[f.key] = form[f.key]; });
                await api.patch(`${endpoint}/${initial.id}`, payload);
                toast.success("Entry updated");
            }
            onSaved?.(); onClose?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid={`${testid}-form-dialog`} className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[600px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h2 className="font-display text-[18px] font-bold text-[#1F1F1F]">{mode === "create" ? `New — ${title}` : `Edit entry`}</h2>
                    <button data-testid={`${testid}-form-close`} onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-3.5 form-pane">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <RField label="Client" required>
                            <select data-testid={`${testid}-form-client`} value={form.client_id || ""} disabled={mode === "edit" || !!lockedClientId}
                                onChange={(e) => set("client_id", e.target.value)} className="fi">
                                <option value="">Select…</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </RField>
                        <RField label="Building" required>
                            <select data-testid={`${testid}-form-building`} value={form.building_id || ""} disabled={mode === "edit"}
                                onChange={(e) => set("building_id", e.target.value)} className="fi">
                                <option value="">Select…</option>
                                {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </RField>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {fields.map((f) => f.type === "checkbox" ? (
                            <label key={f.key} className="flex items-center gap-2 text-[12.5px] font-medium text-[#4A4640] pt-1">
                                <input data-testid={`${testid}-form-${f.key}`} type="checkbox" checked={!!form[f.key]}
                                    onChange={(e) => set(f.key, e.target.checked)} className="rounded" />
                                {f.label}
                            </label>
                        ) : (
                            <RField key={f.key} label={f.label} required={f.required} full={f.full || f.type === "textarea"}>
                                {f.type === "select" ? (
                                    <select data-testid={`${testid}-form-${f.key}`} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="fi">
                                        <option value="">Select…</option>
                                        {(f.options || meta[f.metaKey] || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                ) : f.type === "textarea" ? (
                                    <textarea data-testid={`${testid}-form-${f.key}`} rows={2} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="fi" />
                                ) : (
                                    <input data-testid={`${testid}-form-${f.key}`} type={f.type === "date" ? "date" : "text"} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="fi" />
                                )}
                            </RField>
                        ))}
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex items-center justify-end gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <button onClick={onClose} disabled={busy} data-testid={`${testid}-form-cancel`} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button onClick={save} disabled={busy} data-testid={`${testid}-form-save`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save"}
                    </button>
                </div>
                <style>{`
                    .form-pane .fi { width: 100%; padding: 8px 11px; background: #fff; border: 1px solid #E5E0DA; border-radius: 8px; font-size: 12.5px; color: #1F1F1F; outline: none; }
                    .form-pane .fi:focus { border-color: var(--brand-accent, #C75A1B); box-shadow: 0 0 0 3px #FBEFE5; }
                `}</style>
            </div>
        </div>
    );
}

function RField({ label, required, full, children }) {
    return (
        <label className={`block ${full ? "sm:col-span-2" : ""}`}>
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}
